import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setForex } from '../../../../features/forex/forexSlice.js';
import "./ProjectOverviewListingSingleActivities.css";
import axios from 'axios';
import Modal from "react-modal";
import { DateRangePicker } from "rsuite";
import AES from "crypto-js/aes";
import { Link, useParams } from 'react-router-dom';
import { toast } from "react-toastify";
import TimeIccon from "../../../../images/icons/clock.svg";
import DollarIccon from "../../../../images/icons/billable.svg";
import CloseDollarIccon from "../../../../images/icons/non-billable.svg";
import PurseMmoney from "../../../../images/icons/purses-money.svg";
import NotepadIcon from "../../../../images/icons/notepad.svg";
import DollarReverse from "../../../../images/icons/dollar-reverse.svg";
import crssrImage from "../../../../images/singleinvoice/cross-red.svg";
import { isMvpEnv } from "../../../../utils/brandLogo";

const ClientActivities = (props) => {
    const dispatch = useDispatch();
    const { forex } = useSelector((state) => state.forex);
    const settings = JSON.parse(localStorage.getItem("settings"));
    const { projectId } = useParams();
    const projectLoading = props.isLoadingProject;

    const totalHours = props?.project?.timers && props?.project?.timers.reduce((total, timer) => total + timer.duration, 0);
    const totalCost = (props?.project?.timers || []).reduce((sum, timer) => {
        return sum + (timer.totalCost || 0);
    }, 0);
    const totalBudget = (props?.project?.sprints || []).reduce((sum, sprint) => {
        return sum + (sprint.budget || 0);
    }, 0);

    const changeDurationFormat = (sec) => {
        let second = sec;
        const hours = Math.floor(second / 3600);
        const minutes = Math.floor((second % 3600) / 60);
        const seconds = second % 60;
        const formattedDuration = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        return formattedDuration;
    }

    const Roi = (Cbudget, cCost) => {
        const budget = Cbudget;
        const cost = cCost;
        if (cost === 0 && budget === 0) {
            return "0%";
        } else if (cost === 0 && budget !== 0) {
            return "N/A";
        } else {
            const rev = budget - cost;
            const Rvalue = (rev / cost) * 100;
            return isNaN(Rvalue) ? "0%" : Rvalue.toFixed(2) + "%";
        }
    }

    function ConvertHelper(currencyFrom, currencyTo, amount, forex) {
        const currencyFromRate = forex?.[currencyFrom];
        const currencyToRate = forex?.[currencyTo];
        if (!currencyFromRate || !currencyToRate) return Number(amount) || 0;
        return (amount / currencyFromRate) * currencyToRate;
    }

    useEffect(() => {
        if (!forex) dispatch(setForex());
    }, [forex, dispatch]);

    const preferredCurrency = props.project?.client_name?.preferred_currency || 'USD';
    const convertedBudget = ConvertHelper(preferredCurrency, settings?.currency, totalBudget, forex);

    // State for animated values
    const [displayedHours, setDisplayedHours] = useState(0);
    const [displayedBillableHours, setDisplayedBillableHours] = useState(0);
    const [displayedNonBillableHours, setDisplayedNonBillableHours] = useState(0);
    const [displayedBudget, setDisplayedBudget] = useState(0);
    const [displayedCost, setDisplayedCost] = useState(0);
    const [displayedRoi, setDisplayedRoi] = useState("0%");

    // Refs for animation
    const hoursAnimationRef = useRef(null);
    const billableAnimationRef = useRef(null);
    const nonBillableAnimationRef = useRef(null);
    const budgetAnimationRef = useRef(null);
    const costAnimationRef = useRef(null);
    const roiAnimationRef = useRef(null);

    // Animation function
    const animateValue = (start, end, duration, setValue, animationRef) => {
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }

        const startTime = performance.now();
        const isNumber = typeof end === 'number' && !isNaN(end);

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // Ease-out cubic function
            const easeOutCubic = 1 - Math.pow(1 - progress, 3);
            
            if (isNumber) {
                const current = start + (end - start) * easeOutCubic;
                setValue(current);
            } else {
                setValue(end);
            }

            if (progress < 1) {
                animationRef.current = requestAnimationFrame(animate);
            } else {
                setValue(end);
            }
        };

        animationRef.current = requestAnimationFrame(animate);
    };

    // Calculate billable and non-billable hours
    const billableHours = props.project?.is_billable && totalHours ? totalHours : 0;
    const nonBillableHours = !props.project?.is_billable && totalHours ? totalHours : 0;

    // Animate hours
    useEffect(() => {
        if (!projectLoading && totalHours !== undefined) {
            animateValue(displayedHours, totalHours || 0, 1000, setDisplayedHours, hoursAnimationRef);
        }
    }, [totalHours, projectLoading]);

    // Animate billable hours
    useEffect(() => {
        if (!projectLoading && billableHours !== undefined) {
            animateValue(displayedBillableHours, billableHours || 0, 1000, setDisplayedBillableHours, billableAnimationRef);
        }
    }, [billableHours, projectLoading]);

    // Animate non-billable hours
    useEffect(() => {
        if (!projectLoading && nonBillableHours !== undefined) {
            animateValue(displayedNonBillableHours, nonBillableHours || 0, 1000, setDisplayedNonBillableHours, nonBillableAnimationRef);
        }
    }, [nonBillableHours, projectLoading]);

    // Animate budget
    useEffect(() => {
        if (!projectLoading && totalBudget !== undefined && preferredCurrency) {
            const budgetValue = totalBudget ? ConvertHelper(props.project?.client_name?.preferred_currency, preferredCurrency, totalBudget, forex) : 0;
            animateValue(displayedBudget, budgetValue, 1000, setDisplayedBudget, budgetAnimationRef);
        }
    }, [totalBudget, preferredCurrency, projectLoading, forex, props.project?.client_name?.preferred_currency]);

    // Animate cost
    useEffect(() => {
        if (!projectLoading && totalCost !== undefined) {
            animateValue(displayedCost, totalCost || 0, 1000, setDisplayedCost, costAnimationRef);
        }
    }, [totalCost, projectLoading]);

    // Animate ROI
    useEffect(() => {
        if (!projectLoading && totalBudget !== undefined && totalCost !== undefined) {
            const roiValue = Roi(totalBudget || 0, totalCost || 0);
            if (typeof roiValue === 'string' && roiValue.includes('%') && roiValue !== 'N/A') {
                const numericValue = parseFloat(roiValue.replace('%', ''));
                if (!isNaN(numericValue)) {
                    const currentRoi = typeof displayedRoi === 'number' ? displayedRoi : parseFloat(String(displayedRoi).replace('%', '')) || 0;
                    animateValue(currentRoi, numericValue, 1000, setDisplayedRoi, roiAnimationRef);
                } else {
                    setDisplayedRoi(roiValue);
                }
            } else {
                setDisplayedRoi(roiValue);
            }
        }
    }, [totalBudget, totalCost, projectLoading]);

    // Cleanup animations on unmount
    useEffect(() => {
        return () => {
            if (hoursAnimationRef.current) cancelAnimationFrame(hoursAnimationRef.current);
            if (billableAnimationRef.current) cancelAnimationFrame(billableAnimationRef.current);
            if (nonBillableAnimationRef.current) cancelAnimationFrame(nonBillableAnimationRef.current);
            if (budgetAnimationRef.current) cancelAnimationFrame(budgetAnimationRef.current);
            if (costAnimationRef.current) cancelAnimationFrame(costAnimationRef.current);
            if (roiAnimationRef.current) cancelAnimationFrame(roiAnimationRef.current);
        };
    }, []);

    // ── HubSpot hours sync ───────────────────────────────────────────────────
    const HubSpotLogo = ({ size = 16 }) => (
        <svg width={size} height={size} viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
            <path d="M426.6 241.3c-18.7-26.5-45.7-43.2-76.2-48.1v-54.7c13.5-5.8 22.9-19.2 22.9-34.8V92.3C373.3 69 354.3 50 331 50h-11.4c-23.3 0-42.3 19-42.3 42.3v11.4c0 15.6 9.4 29 22.9 34.8v54.7c-22.4 3.4-43.5 12.8-61.5 27.7L117.2 141c1-3.7 1.6-7.6 1.6-11.6 0-25.3-20.5-45.8-45.8-45.8S27.2 104.1 27.2 129.4s20.5 45.8 45.8 45.8c10.6 0 20.4-3.6 28.1-9.6l119.1 78.3c-11.2 20.4-17.4 43.7-17.4 67.8 0 28.3 8.4 55.2 23.9 78l-36.2 36.2c-2.8-.9-5.7-1.4-8.8-1.4-16.5 0-29.9 13.4-29.9 29.9S165.2 484 181.7 484s29.9-13.4 29.9-29.9c0-3.1-.5-6.1-1.4-8.8l35.8-35.8c23.3 16.3 51.1 25.5 80.6 25.5 78.7 0 142.6-63.9 142.6-142.6 0-21.2-4.7-41.7-13.4-60.1l-8.2 8.6-.9-1.6zM325.6 392.5c-55.8 0-101.1-45.3-101.1-101.1s45.3-101.1 101.1-101.1 101.1 45.3 101.1 101.1-45.3 101.1-101.1 101.1z" fill="#ff7a59"/>
        </svg>
    );

    const [hsHoursSyncEnabled, setHsHoursSyncEnabled] = useState(false);
    const [hsHoursModalOpen, setHsHoursModalOpen] = useState(false);
    const [hsHoursSyncing, setHsHoursSyncing] = useState(false);
    const [hsHoursConfirmed, setHsHoursConfirmed] = useState(false);
    const [hsHoursLastSynced, setHsHoursLastSynced] = useState(null);
    const [hsHoursNextSync, setHsHoursNextSync] = useState(null);
    const [hsHoursMapped, setHsHoursMapped] = useState(false);
    const [hsHoursTotal, setHsHoursTotal] = useState(null);

    // Auto-sync settings
    const [hsAutoSyncEnabled, setHsAutoSyncEnabled] = useState(false);
    const [hsAutoSyncInterval, setHsAutoSyncInterval] = useState(60);
    const [hsAutoSyncSaving, setHsAutoSyncSaving] = useState(false);

    // Missing HubSpot property state
    const [hsMissingProperty, setHsMissingProperty] = useState(false);
    const [hsCreatingProperty, setHsCreatingProperty] = useState(false);

    const INTERVAL_OPTIONS = [
        { label: "Every 15 minutes", value: 15 },
        { label: "Every 30 minutes", value: 30 },
        { label: "Every 1 hour", value: 60 },
        { label: "Every 3 hours", value: 180 },
        { label: "Every 6 hours", value: 360 },
        { label: "Every 12 hours", value: 720 },
        { label: "Every 24 hours", value: 1440 },
    ];

    const token = props.token;
    const hsProjectId = props.project?._id;

    useEffect(() => {
        if (!token || !hsProjectId || projectLoading) return;
        axios.get("/api/integrations/hubspot/config", { headers: { Authorization: `Bearer ${token}` } })
            .then(r => { setHsHoursSyncEnabled(!!r.data?.sync_hours_enabled); })
            .catch(() => {});
        axios.get(`/api/integrations/hubspot/project/${hsProjectId}/sync-hours`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then(r => {
            if (r.data?.status === "mapped") {
                setHsHoursMapped(true);
                setHsHoursLastSynced(r.data.hours_last_synced_at || null);
                setHsHoursNextSync(r.data.hours_next_sync_at || null);
                setHsHoursTotal(r.data.total_hours ?? null);
                setHsAutoSyncEnabled(r.data.hours_auto_sync_enabled || false);
                setHsAutoSyncInterval(r.data.hours_sync_interval_minutes || 60);
            }
        }).catch(() => {});
    }, [token, hsProjectId, projectLoading]);

    const openHsHoursModal = () => {
        setHsHoursConfirmed(false);
        setHsMissingProperty(false);
        setHsHoursModalOpen(true);
    };

    const handleHsHoursSync = async () => {
        setHsHoursSyncing(true);
        setHsMissingProperty(false);
        try {
            const r = await axios.post(
                `/api/integrations/hubspot/project/${hsProjectId}/sync-hours`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setHsHoursLastSynced(r.data.hours_last_synced_at);
            setHsHoursNextSync(r.data.hours_next_sync_at || null);
            setHsHoursTotal(r.data.total_hours);
            setHsHoursMapped(true);
            toast.success("Hours synced to HubSpot!", { position: "bottom-right", autoClose: 3000 });
            setHsHoursModalOpen(false);
        } catch (err) {
            if (err?.response?.data?.error_code === "MISSING_HS_PROPERTY") {
                setHsMissingProperty(true);
            } else {
                toast.error(err?.response?.data?.message || "Failed to sync hours.", { position: "bottom-right", autoClose: 3000 });
            }
        } finally {
            setHsHoursSyncing(false);
        }
    };

    const handleCreateHsProperty = async () => {
        setHsCreatingProperty(true);
        try {
            await axios.post(
                `/api/integrations/hubspot/ensure-hours-property`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Property created! Syncing hours now…", { position: "bottom-right", autoClose: 3000 });
            setHsMissingProperty(false);
            // Auto-retry sync
            setHsHoursConfirmed(true);
            setTimeout(() => handleHsHoursSync(), 400);
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to create property.", { position: "bottom-right", autoClose: 4000 });
        } finally {
            setHsCreatingProperty(false);
        }
    };

    const handleAutoSyncSave = async (enabled, interval) => {
        setHsAutoSyncSaving(true);
        try {
            const r = await axios.put(
                `/api/integrations/hubspot/project/${hsProjectId}/auto-sync`,
                { enabled, interval_minutes: interval },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setHsAutoSyncEnabled(r.data.hours_auto_sync_enabled);
            setHsAutoSyncInterval(r.data.hours_sync_interval_minutes);
            setHsHoursNextSync(r.data.hours_next_sync_at || null);
            toast.success(enabled ? "Auto-sync enabled!" : "Auto-sync disabled.", { position: "bottom-right", autoClose: 2500 });
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to update auto-sync.", { position: "bottom-right", autoClose: 3000 });
        } finally {
            setHsAutoSyncSaving(false);
        }
    };

    const formatSyncDate = (iso) => {
        if (!iso) return null;
        const d = new Date(iso);
        return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    };

    const hsModalStyle = {
        content: {
            width: "34%",
            position: "fixed",
            top: "50%",
            left: "50%",
            parent: document.querySelector(".admin-outer.time.tracker"),
        },
    };

    return (
        <>
        <div className="client-activities-outer">
            <style>
                {`
                    @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.5; }
                    }
                `}
            </style>
            <div className="client-activity-columns">
                <div className="activity-column">
                    <div className="activity-with-image">
                        <div className="activity-name">
                            {isMvpEnv() ? "Total Hours to Date" : "Total Hours"}
                        </div>
                        <img src={TimeIccon} alt="time-iccon" />
                    </div>
                    <div className="activity-count">
                        {projectLoading ? (
                            <div
                                style={{
                                    width: "140px",
                                    height: "52px",
                                    borderRadius: "4px",
                                    backgroundColor: "#e5e7eb",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                }}
                            />
                        ) : (
                            <span>{displayedHours ? changeDurationFormat(Math.floor(displayedHours)) : '00:00:00'}</span>
                        )}
                    </div>
                    {hsHoursMapped && hsHoursSyncEnabled && !projectLoading && (
                        <button
                            onClick={openHsHoursModal}
                            style={{
                                display: "inline-flex", alignItems: "center", gap: "5px",
                                marginTop: "10px", background: "none",
                                border: "1.5px solid #e8e8e8", borderRadius: "8px",
                                padding: "5px 10px", fontSize: "12px", fontWeight: 500,
                                color: "#454F59", cursor: "pointer",
                                transition: "all 0.18s",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = "#ff7a59"; e.currentTarget.style.color = "#ff7a59"; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = "#e8e8e8"; e.currentTarget.style.color = "#454F59"; }}
                        >
                            <HubSpotLogo size={13} /> Sync
                        </button>
                    )}
                </div>
                <div className="activity-column">
                    <div className="activity-with-image">
                        <div className="activity-name">
                            {isMvpEnv() ? "Total Billable Hours" : "Total Billable"}
                        </div>
                        <img src={DollarIccon} alt="dollar-iccon" />
                    </div>
                    <div className="activity-count">
                        {projectLoading ? (
                            <div
                                style={{
                                    width: "140px",
                                    height: "52px",
                                    borderRadius: "4px",
                                    backgroundColor: "#e5e7eb",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                }}
                            />
                        ) : (
                            <span>
                                {displayedBillableHours ? changeDurationFormat(Math.floor(displayedBillableHours)) : '00:00:00'}
                            </span>
                        )}
                    </div>
                </div>
                <div className="activity-column">
                    <div className="activity-with-image">
                        <div className="activity-name">
                            {isMvpEnv() ? "Total Non-Billable Hours" : "Total Non-Billable"}
                        </div>
                        <img src={CloseDollarIccon} alt="close-dollar-iccon" />
                    </div>
                    <div className="activity-count">
                        {projectLoading ? (
                            <div
                                style={{
                                    width: "140px",
                                    height: "52px",
                                    borderRadius: "4px",
                                    backgroundColor: "#e5e7eb",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                }}
                            />
                        ) : (
                            <span>
                                {displayedNonBillableHours ? changeDurationFormat(Math.floor(displayedNonBillableHours)) : '00:00:00'}
                            </span>
                        )}
                    </div>
                </div>
                <div className="activity-column">
                    <div className="activity-with-image">
                        <div className="activity-name">
                            Total Budget
                        </div>
                        <img src={PurseMmoney} alt="purses-of-money" />
                    </div>
                    <div className="activity-count">
                        {props.isLoadingProject ? (
                            <div
                                style={{
                                    width: "160px",
                                    height: "52px",
                                    borderRadius: "4px",
                                    backgroundColor: "#e5e7eb",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                }}
                            />
                        ) : (
                            <>
                                {preferredCurrency ? (
                                    new Intl.NumberFormat("en-US", {
                                        style: "currency",
                                        currency: preferredCurrency || 'USD',
                                    }).format(displayedBudget || 0)
                                ) : (
                                    displayedBudget ? displayedBudget.toFixed(2) : '0.00'
                                )}
                            </>
                        )}
                    </div>
                </div>
                <div className="activity-column">
                    <div className="activity-with-image">
                        <div className="activity-name">
                            {isMvpEnv() ? "Cost to Date" : "Total Cost"}
                        </div>
                        <img src={NotepadIcon} alt="notepad-iccon" />
                    </div>
                    <div className="activity-count">
                        {projectLoading ? (
                            <div
                                style={{
                                    width: "160px",
                                    height: "52px",
                                    borderRadius: "4px",
                                    backgroundColor: "#e5e7eb",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                }}
                            />
                        ) : (
                            props.project?.client_name?.preferred_currency ? (
                                new Intl.NumberFormat("en-US", {
                                    style: "currency",
                                    currency: preferredCurrency || "USD",
                                }).format(displayedCost || 0)
                            ) : (
                                (displayedCost || 0).toFixed(2)
                            )
                        )}
                    </div>
                </div>
                <div className="activity-column">
                    <div className="activity-with-image">
                        <div className="activity-name">
                            {isMvpEnv() ? "ROI to Date" : "ROI"}
                        </div>
                        <img src={DollarReverse} alt="time-iccon" />
                    </div>
                    <div className="activity-count">
                        {(props.isLoadingProject) ? (
                            <div
                                style={{
                                    width: "100px",
                                    height: "52px",
                                    borderRadius: "4px",
                                    backgroundColor: "#e5e7eb",
                                    animation: "pulse 1.5s ease-in-out infinite",
                                }}
                            />
                        ) : (
                            typeof displayedRoi === 'number' && !isNaN(displayedRoi) ? (
                                displayedRoi.toFixed(2) + "%"
                            ) : (
                                displayedRoi
                            )
                        )}
                    </div>
                </div>

            </div>
        </div>

        {/* ── HubSpot Hours Sync Modal ── */}

        <Modal
            className="client-modal"
            isOpen={hsHoursModalOpen}
            onRequestClose={() => setHsHoursModalOpen(false)}
            contentLabel="Sync Hours to HubSpot"
            style={hsModalStyle}
        >
            <div className="modal">
                <div className="modal-close-invite outter-modal-main">
                    <a onClick={() => setHsHoursModalOpen(false)} style={{ cursor: "pointer" }}>
                        <img src={crssrImage} alt="Close" />
                    </a>
                </div>
                <div className="modal-client-head">
                    <h2 style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <HubSpotLogo size={20} /> Sync Hours to HubSpot
                    </h2>
                </div>
                <div className="form-field-client-outer">
                    {/* Detail card */}
                    <div style={{
                        background: "#f7f8ff", borderRadius: "10px", padding: "14px 16px",
                        marginBottom: "16px", display: "flex", gap: "12px", alignItems: "center",
                        border: "1px solid #e0e5ff",
                    }}>
                        <div style={{
                            width: "40px", height: "40px", borderRadius: "50%", background: "#6479F8",
                            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: "18px", fontWeight: 700, flexShrink: 0,
                        }}>H</div>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: "#282828" }}>
                                {props.project?.project_name || "Project"}
                            </div>
                            <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>
                                HubSpot Project · "Hours for project" property
                            </div>
                        </div>
                    </div>

                    {/* Summary rows */}
                    <div style={{ borderRadius: "10px", border: "1px solid #ebebeb", overflow: "hidden", marginBottom: "16px" }}>
                        <div style={{ display: "flex", padding: "9px 14px", fontSize: "13px", borderBottom: "1px solid #f3f4f6" }}>
                            <span style={{ color: "#9ca3af", width: "140px", flexShrink: 0, fontWeight: 500 }}>Total Tracked Hours</span>
                            <span style={{ color: "#282828", fontWeight: 600 }}>
                                {hsHoursTotal !== null ? `${hsHoursTotal} hrs` : (totalHours ? `${Math.round(totalHours / 3600 * 100) / 100} hrs` : "0 hrs")}
                            </span>
                        </div>
                        <div style={{ display: "flex", padding: "9px 14px", fontSize: "13px", background: "#fafbff", borderBottom: "1px solid #f3f4f6" }}>
                            <span style={{ color: "#9ca3af", width: "140px", flexShrink: 0, fontWeight: 500 }}>Last Synced</span>
                            <span style={{ color: hsHoursLastSynced ? "#282828" : "#c0c4cc", fontWeight: 500 }}>
                                {hsHoursLastSynced ? formatSyncDate(hsHoursLastSynced) : "Never synced"}
                            </span>
                        </div>
                        {hsAutoSyncEnabled && hsHoursNextSync && (
                            <div style={{ display: "flex", padding: "9px 14px", fontSize: "13px" }}>
                                <span style={{ color: "#9ca3af", width: "140px", flexShrink: 0, fontWeight: 500 }}>Next Auto-Sync</span>
                                <span style={{ color: "#16a34a", fontWeight: 500 }}>{formatSyncDate(hsHoursNextSync)}</span>
                            </div>
                        )}
                    </div>

                    {/* Auto-sync section */}
                    <div style={{
                        border: "1px solid #e0e5ff", borderRadius: "10px",
                        padding: "14px 16px", marginBottom: "16px", background: hsAutoSyncEnabled ? "#f7f8ff" : "#fff",
                    }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: hsAutoSyncEnabled ? "12px" : "0" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <label style={{ position: "relative", display: "inline-block", width: "36px", height: "20px", flexShrink: 0 }}>
                                    <input
                                        type="checkbox"
                                        checked={hsAutoSyncEnabled}
                                        onChange={e => {
                                            const val = e.target.checked;
                                            setHsAutoSyncEnabled(val);
                                            handleAutoSyncSave(val, hsAutoSyncInterval);
                                        }}
                                        style={{ opacity: 0, width: 0, height: 0 }}
                                        disabled={hsAutoSyncSaving}
                                    />
                                    <span style={{
                                        position: "absolute", cursor: hsAutoSyncSaving ? "not-allowed" : "pointer",
                                        top: 0, left: 0, right: 0, bottom: 0,
                                        background: hsAutoSyncEnabled ? "#6479F8" : "#e0e0e0",
                                        borderRadius: "20px", transition: "0.2s",
                                    }}>
                                        <span style={{
                                            position: "absolute", content: '""',
                                            height: "14px", width: "14px",
                                            left: hsAutoSyncEnabled ? "19px" : "3px", bottom: "3px",
                                            background: "#fff", borderRadius: "50%", transition: "0.2s",
                                            display: "block",
                                        }} />
                                    </span>
                                </label>
                                <div>
                                    <div style={{ fontSize: "13px", fontWeight: 600, color: "#282828" }}>Auto-Sync Hours</div>
                                    <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "1px" }}>
                                        {hsAutoSyncEnabled ? "Automatically pushes hours on schedule" : "Enable to push hours automatically"}
                                    </div>
                                </div>
                            </div>
                            {hsAutoSyncSaving && <span style={{ fontSize: "11px", color: "#9ca3af" }}>Saving…</span>}
                        </div>

                        {hsAutoSyncEnabled && (
                            <div>
                                <label style={{ fontSize: "12px", fontWeight: 600, color: "#454F59", display: "block", marginBottom: "6px" }}>
                                    Sync Interval
                                </label>
                                <select
                                    value={hsAutoSyncInterval}
                                    onChange={e => {
                                        const val = Number(e.target.value);
                                        setHsAutoSyncInterval(val);
                                        handleAutoSyncSave(true, val);
                                    }}
                                    disabled={hsAutoSyncSaving}
                                    style={{
                                        width: "100%", padding: "8px 12px", border: "1px solid #e0e0e0",
                                        borderRadius: "8px", fontSize: "13px", color: "#282828",
                                        background: "#fff", outline: "none", cursor: "pointer",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    {INTERVAL_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Missing property warning */}
                    {hsMissingProperty && (
                        <div style={{
                            background: "#fff8ed", border: "1.5px solid #f59e0b", borderRadius: "10px",
                            padding: "14px 16px", marginBottom: "16px",
                        }}>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                                <span style={{ fontSize: "20px", lineHeight: 1, flexShrink: 0 }}>⚠️</span>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, fontSize: "13px", color: "#92400e", marginBottom: "4px" }}>
                                        Missing HubSpot Property
                                    </div>
                                    <div style={{ fontSize: "12px", color: "#78350f", lineHeight: 1.5, marginBottom: "12px" }}>
                                        The property <strong>hours_for_project</strong> does not exist on your HubSpot Projects object.
                                        Click below to create it automatically, then sync will retry.
                                    </div>
                                    <button
                                        onClick={handleCreateHsProperty}
                                        disabled={hsCreatingProperty}
                                        style={{
                                            background: hsCreatingProperty ? "#fbbf24" : "#f59e0b",
                                            color: "#fff", border: "none", borderRadius: "7px",
                                            padding: "7px 16px", fontSize: "13px", fontWeight: 600,
                                            cursor: hsCreatingProperty ? "not-allowed" : "pointer",
                                            opacity: hsCreatingProperty ? 0.7 : 1,
                                        }}
                                    >
                                        {hsCreatingProperty ? "Creating…" : "Create Property in HubSpot"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Manual sync confirm checkbox */}
                    {!hsMissingProperty && (
                    <div style={{
                        display: "flex", alignItems: "flex-start", gap: "10px",
                        padding: "14px", background: "#f7f8ff", borderRadius: "8px",
                        border: "1px solid #e0e5ff", marginBottom: "0",
                    }}>
                        <input
                            type="checkbox"
                            id="hs-hours-confirm"
                            checked={hsHoursConfirmed}
                            onChange={e => setHsHoursConfirmed(e.target.checked)}
                            style={{ marginTop: "2px", width: "16px", height: "16px", cursor: "pointer", flexShrink: 0, accentColor: "#6479F8" }}
                        />
                        <label htmlFor="hs-hours-confirm" style={{ fontSize: "13px", color: "#454F59", cursor: "pointer", lineHeight: 1.5 }}>
                            Confirm and sync current tracked hours to HubSpot now.
                        </label>
                    </div>
                    )}

                    <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "20px" }}>
                        <button
                            onClick={() => { setHsHoursModalOpen(false); setHsMissingProperty(false); }}
                            style={{
                                background: "none", border: "1.5px solid #e0e0e0", borderRadius: "8px",
                                padding: "8px 18px", fontSize: "14px", color: "#454F59", cursor: "pointer",
                            }}
                        >Cancel</button>
                        {!hsMissingProperty && (
                        <button
                            onClick={handleHsHoursSync}
                            disabled={hsHoursSyncing || !hsHoursConfirmed}
                            style={{
                                background: (hsHoursSyncing || !hsHoursConfirmed) ? undefined : "#6479F8",
                                opacity: (hsHoursSyncing || !hsHoursConfirmed) ? 0.55 : 1,
                                color: "#fff", border: "none", borderRadius: "8px",
                                padding: "8px 22px", fontSize: "14px", fontWeight: 600,
                                cursor: (hsHoursSyncing || !hsHoursConfirmed) ? "not-allowed" : "pointer",
                                backgroundColor: "#6479F8",
                            }}
                        >
                            {hsHoursSyncing ? "Syncing…" : "Sync to HubSpot"}
                        </button>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
        </>
    )
};

export default ClientActivities; 